import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, MapPin, User } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../AdminLayout";
import { adminAxios, formatErr } from "../api";
import { StatusPill } from "./AdminDashboard";
import { useCatalog } from "../../contexts/CatalogContext";

export default function AdminCustomerDetail() {
  const { phone } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const { categories: CATEGORIES } = useCatalog();

  useEffect(() => {
    adminAxios.get(`/customers/${phone}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatErr(e)));
  }, [phone]);

  if (!data) return <AdminLayout title="Customer"><div className="text-slate-500">Loading…</div></AdminLayout>;

  const bookings = filter === "all" ? data.bookings : data.bookings.filter((b) => b.category === filter);

  return (
    <AdminLayout title={data.name}>
      <button onClick={() => navigate(-1)} className="mb-4 text-sm font-semibold text-slate-500 hover:text-[#1A3C6E] flex items-center gap-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-5 mb-4">
        <div className="w-14 h-14 rounded-full bg-[#1A3C6E] text-white flex items-center justify-center font-bold text-xl">
          {data.name?.charAt(0)?.toUpperCase()}
        </div>
        <div className="flex-1">
          <h2 className="font-display font-bold text-xl text-slate-900">{data.name}</h2>
          <div className="text-sm text-slate-500 mt-1 capitalize">{data.city} • {data.segment}</div>
          <div className="mt-3 flex gap-3 text-sm">
            <a href={`tel:${data.phone}`} className="inline-flex items-center gap-1.5 text-[#1A3C6E] font-semibold" data-testid="customer-call">
              <Phone className="w-4 h-4" /> {data.phone}
            </a>
            <a href={`https://wa.me/91${data.phone}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#22C55E] font-semibold" data-testid="customer-whatsapp">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Total bookings</div>
          <div className="font-display font-extrabold text-3xl text-[#1A3C6E]">{data.total_bookings}</div>
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        {[{ id: "all", label: "All" }, ...CATEGORIES.map((c) => ({ id: c.id, label: c.short }))].map((t) => (
          <button
            key={t.id}
            data-testid={`customer-cat-${t.id}`}
            onClick={() => setFilter(t.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
              filter === t.id ? "bg-[#1A3C6E] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-[#1A3C6E]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm" data-testid="customer-bookings-table">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="px-5 py-2.5">ID</th>
              <th className="px-5 py-2.5">Service</th>
              <th className="px-5 py-2.5">Date</th>
              <th className="px-5 py-2.5">Slot</th>
              <th className="px-5 py-2.5">Price</th>
              <th className="px-5 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">No bookings in this category.</td></tr>}
            {bookings.map((b) => (
              <tr key={b.booking_id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3"><Link to={`/admin/bookings/${b.booking_id}`} className="font-semibold text-[#1A3C6E] hover:underline">{b.booking_id}</Link></td>
                <td className="px-5 py-3">{b.service_name} • <span className="text-slate-500">{b.brand}</span></td>
                <td className="px-5 py-3">{b.date}</td>
                <td className="px-5 py-3 capitalize">{b.slot}</td>
                <td className="px-5 py-3">₹{b.price}</td>
                <td className="px-5 py-3"><StatusPill status={b.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
